'use client'

import { memo, useEffect, useRef } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { motion, useAnimation, useInView, type Variants } from 'motion/react'
import type { TypeLang } from '@/schema'
import { mq, vw, getTransition, getP50, imgVariants, createUnderline } from '@/styles'

type TypeLocalizedString = string | Record<TypeLang, string>

interface ISectionTwoCols {
  cols: { name: TypeLocalizedString, pathname: TypeLocalizedString, src: string }[]
  className?: string
}

export const SectionTwoCols = memo<ISectionTwoCols>(({ cols, className = '' }) => {
  const controls = useAnimation()
  const { lang }: { lang: TypeLang } = useParams()
  const ref = useRef(null)
  const isInView = useInView(ref, { margin: '0px 0px -100px 0px' })


  const handleLoad = () => {
    controls.start('animate')
    controls.start('animateText')
  }
  useEffect(() => {
    if(isInView) {
      handleLoad()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[controls, isInView])

  return (
    <Aside ref={ref} className={className}>
      <a href={typeof cols[0].pathname  === 'string' ? cols[0].pathname : cols[0].pathname[lang]}>
        <motion.figure variants={imgVariants} animate={controls} initial={'initial'}>
          {/*eslint-disable-next-line @next/next/no-img-element*/}
          <img src={cols[0].src} alt='bg-image' onLoad={handleLoad}  sizes='(min-width: 768px) 50vw, 100vw'/>
        </motion.figure>
        <motion.div variants={titleVariants} animate={controls} initial={'initial'}>{typeof cols[0].name === 'string' ? cols[0].name : cols[0].name[lang]}<span></span></motion.div>
      </a>
      <a href={typeof cols[1].pathname  === 'string' ? cols[1].pathname : cols[1].pathname[lang]}>
        <motion.figure variants={imgVariants} animate={controls} initial={'initial'}>
          {/*eslint-disable-next-line @next/next/no-img-element*/}
          <img src={cols[1].src} alt='bg-image' onLoad={handleLoad}  sizes='(min-width: 768px) 50vw, 100vw'/>
        </motion.figure>
        <motion.div variants={titleVariants} animate={controls} initial={'initial'}>{typeof cols[1].name === 'string' ? cols[1].name : cols[1].name[lang]}<span></span></motion.div>
      </a>
    </Aside>
  )
})


const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  position: relative;

  ${mq.greaterThan('tablet')}{
    flex-wrap: nowrap;
    flex-direction: row;
  }

  a {
    background-color: var(--color-img-placeholder);
    display: inline-block;
    overflow: hidden;
    position: relative;
    text-decoration: none;
    width: 100%;

    ${mq.greaterThan('tablet')}{
      width: 50%;
    }

    > div {
      ${createUnderline({ reverse: true, color: 'var(--color-white)' })}
      ${getP50()}
      color: var(--color-white);
      left: 50%;
      position: absolute;
      top: 50%;
    }

    figure {
      height: ${vw(298, 'mobile')};
      overflow: hidden;
      position: relative;
      width: 100vw;

      ${mq.greaterThan('tablet')}{
        height: ${vw(510, 'desktop')};
        width: 100%;
      }

      img {
        height: 100%;
        transition: 1000ms transform var(--transition-ease);
      }
    }

    &:hover {
      img {
        transform: scale(1.1);
      }
    }
  }
`

const titleVariants:Variants = {
  initial: {
    y: '20px',
    x: '-50%',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animateText: {
    y: '-50%',
    x: '-50%',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      delay: .3,
      duration: 1
    }
  },
  exit: {
    y: '20px',
    x: '-50%',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  },
}